import { handleAxiosError } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { HubspotProperty, HubspotValue } from '@/client/hubspot/types';
import { UsersReportType } from '@/client/reports';
import { Actions, Subjects } from '@/client/users';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import { ReportsExportModal } from '@/components/reports/ReportsExportModal';
import { useNotifications } from '@/hooks/notifications.hook';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { DateFormats } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton, HoverButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { hubspotTrack } from '@/utils/hubspot';
import { AxiosError } from 'axios';
import moment from 'moment';
import { MenuItem } from 'primereact/menuitem';
import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledFlexContainer = styled(FlexContainer)`
  margin-bottom: var(--default-padding);

  h1 {
    margin: 0;
  }
`;

type ReportsActionHeaderProps = {
  type: UsersReportType;
  visibleColumns: string[];
  filters?: FiltersType | undefined;
  sort?: [string, 'asc' | 'desc'] | null;
  setForceRefetch?: Dispatch<boolean>;
  lastGeneratedAt?: Date;
  showBlank?: boolean;
};

const getBreadcrumbs = (
  account: Account,
  type: string,
  t: TranslationFunctionType,
): MenuItem[] => {
  const items: MenuItem[] = [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
  ];

  switch (type) {
    case UsersReportType.SUMMARY: {
      items.push({
        label: t('reports.summary'),
        url: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        template: AppBreadCrumbTemplate,
      });
      break;
    }
    case UsersReportType.AWARENESS: {
      items.push(
        {
          label: t('sidebar.awareness'),
          className: 'active',
          url: RedirectPaths[RedirectPathsEnum.COURSES](),
          template: AppBreadCrumbTemplate,
        },
        {
          label: t('reports.awareness'),
          url: RedirectPaths[RedirectPathsEnum.REPORTS_AWARENESS](),
          template: AppBreadCrumbTemplate,
        },
      );
      break;
    }
    default:
      break;
  }

  return items;
};

export const ReportsActionHeader: React.FC<ReportsActionHeaderProps> = ({
  type,
  visibleColumns,
  filters,
  sort,
  setForceRefetch,
  lastGeneratedAt,
  showBlank,
}) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);
  const toast = useToast();
  const { can, isRestrictedByRole } = usePermission();
  const canExport = can(Actions.CREATE, Subjects.ACCOUNT_REPORTS);
  const isExportRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.ACCOUNT_REPORTS,
  );

  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    hubspotTrack(HubspotProperty.REPORTS_OPENED, HubspotValue.YES);
  }, []);

  const [isRebuildingData, setIsRebuildingData] = useState<boolean>(false);
  const {
    lastMessage,
    setNotificationParam: setAccountId,
    notificationParam: accountId,
    isConnected,
  } = useNotifications(client.reports.reportsNotifyUrl);

  const handleRebuildSummaryReport = async () => {
    if (account) {
      setAccountId(account.id);
      setIsRebuildingData(true);

      // Wait for a notify connection to establish
      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (isConnected.current) {
            clearInterval(intervalId);
            resolve(true);
          }
        }, 100);
      });

      try {
        if (isConnected.current) {
          await client.reports.rebuildSummaryReport(account.id);
        }
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
        setAccountId(undefined);
        setIsRebuildingData(false);
      }
    }
  };

  useEffect(() => {
    if (!accountId) return;
    handleSyncNotification(lastMessage);
  }, [JSON.stringify(lastMessage)]);

  const handleSyncNotification = async (messages: { data: any } | null) => {
    if (
      !messages?.data?.event ||
      !['account.summary-report.generated'].includes(messages?.data?.event)
    )
      return;

    if (messages.data.event === 'account.summary-report.generated') {
      if (messages.data.success) {
        toast?.success(
          t('toast.success'),
          t('reports.summary.rebuild.success'),
        );

        if (setForceRefetch) {
          setForceRefetch(true);

          setTimeout(() => {
            setForceRefetch(false);
          });
        }
      } else {
        toast?.success(t('toast.error'), messages.data.error);
      }
    }

    setAccountId(undefined);
    setIsRebuildingData(false);
  };

  return (
    <>
      <AppBreadCrumb model={getBreadcrumbs(account as Account, type, t)} />

      <StyledFlexContainer justify="space-between">
        <FlexContainer align="flex-start" direction="column">
          <FlexContainer align="center" justify="flex-start">
            <h1 className="mb-0">{t(`reports.${type}`)}</h1>
          </FlexContainer>
        </FlexContainer>

        {!showBlank && (
          <FlexContainer gap={12} justify="flex-end">
            {type === UsersReportType.SUMMARY && (
              <HoverButton
                label={t('reports.summary.refresh')}
                icon="pi pi-refresh"
                iconPos="left"
                severity="simple"
                type="outlined"
                className="ml-1"
                ariaLabel={t('reports.summary.rebuild')}
                subLabel={
                  lastGeneratedAt
                    ? t('reports.lastUpdatedOn', {
                        date: moment(lastGeneratedAt).format(
                          DateFormats.TIMEDATE_SHORTEST,
                        ),
                      })
                    : undefined
                }
                tooltipOptions={{ position: 'top' }}
                isDisabled={isRebuildingData}
                state={
                  isRebuildingData
                    ? LoadingStatuses.LOADING
                    : LoadingStatuses.IDLE
                }
                onClick={handleRebuildSummaryReport}
              />
            )}
            {(canExport || isExportRestricted) && (
              <ViewOnlyRestrictionWhen restricted={isExportRestricted}>
                <AppButton
                  icon="pi pi-download"
                  label={t('generic.export')}
                  isDisabled={isExportRestricted}
                  onClick={
                    isExportRestricted
                      ? undefined
                      : () => setShowExportModal(true)
                  }
                />
              </ViewOnlyRestrictionWhen>
            )}
          </FlexContainer>
        )}

        <ReportsExportModal
          type={type}
          visibleColumns={visibleColumns}
          filters={filters}
          sort={sort}
          visible={showExportModal}
          onHide={() => setShowExportModal(false)}
        />
      </StyledFlexContainer>
    </>
  );
};
